import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import OrganizationSelector from '../auth/OrganizationSelector';
import OrganizationChecker from '../auth/OrganizationChecker';
import ThemeSelector from '../common/ThemeSelector';
import NotificationBell from './NotificationBell';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  Avatar,
  useTheme,
  useMediaQuery,
  Chip,
  Collapse,
  Badge,
  alpha,
  Tooltip,
  Fade,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Quiz as QuizIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  Storage as StorageIcon,
  Book as BookIcon,
  VideoLibrary as VideoIcon,
  QuestionAnswer as QuestionIcon,
  Inventory as InventoryIcon,
  Settings as SettingsIcon,
  Nature as EcoIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Category as CategoryIcon,
  CreateNewFolder as CreateNewFolderIcon,
  ImageSearch as ImageSearchIcon,
  ExpandLess,
  ExpandMore,
  Logout as LogoutIcon,
  AccountCircle as AccountIcon,
  Notifications as NotificationsIcon,
  Image as ImageIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';

const DRAWER_WIDTH = 260; // Reduzido de 280
const MINI_DRAWER_WIDTH = 64; // Reduzido de 72
const Layout = ({ children }) => {
  const { currentUser, logout, hasPermission, selectOrganization } = useAuth();
  const { isDark } = useCustomTheme();
  const [showOrgSelector, setShowOrgSelector] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedSection, setExpandedSection] = useState('questoes');
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  // Estado do drawer - móvel sempre overlay, desktop pode ser mini ou expandido
  const drawerVariant = isMobile ? 'temporary' : 'permanent';
  const drawerOpen = isMobile ? mobileOpen : desktopOpen;
  const drawerWidth = isMobile
    ? DRAWER_WIDTH
    : desktopOpen
      ? DRAWER_WIDTH
      : MINI_DRAWER_WIDTH;

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setDesktopOpen(!desktopOpen);
    }
  };

  const handleProfileMenuOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleProfileMenuClose();
  };

  const navigateTo = path => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const toggleSection = section => {
    setExpandedSection(expandedSection === section ? '' : section);
  };

  // Configuração dos itens do menu
  const menuSections = [
    {
      id: 'main',
      items: [
        {
          text: 'Dashboard',
          icon: <DashboardIcon />,
          path: '/dashboard',
          requiredPermission: null,
        },
      ],
    },
    {
      id: 'admin',
      title: 'Administração',
      collapsible: true,
      items: [
        {
          text: 'Usuários',
          icon: <PeopleIcon />,
          path: '/usuarios',
          requiredPermission: 'gerenciarUsuarios',
        },
        {
          text: 'Projetos',
          icon: <AssignmentIcon />,
          path: '/projetos',
          requiredPermission: 'gerenciarProjetos',
        },
        {
          text: 'Personagens',
          icon: <PersonIcon />,
          path: '/personagens',
          requiredRole: 'super_admin',
        },
        {
          text: 'Biomas',
          icon: <EcoIcon />,
          path: '/biomas',
          requiredRole: 'super_admin',
        },
        {
          text: 'Logs do sistema',
          icon: <AssignmentIcon />,
          path: '/logs',
          requiredRole: 'super_admin',
        },
        {
          text: 'Administração da organização',
          icon: <SettingsIcon />,
          path: '/admin-organizacao',
          requiredRole: 'org_admin',
        },
        {
          text: 'Configuração de LLM',
          icon: <ScienceIcon />,
          path: '/llm-config',
          requiredRole: 'super_admin',
        },
        {
          text: 'Módulos customizados',
          icon: <CreateNewFolderIcon />,
          path: '/modulos-customizados',
          requiredRole: 'super_admin',
        },
        {
          text: 'Cobrança',
          icon: <AssignmentIcon />,
          path: '/cobranca',
          requiredRole: 'org_admin',
        },
        {
          text: 'Analytics',
          icon: <AssignmentIcon />,
          path: '/analytics',
          requiredRole: 'super_admin',
        },
        {
          text: 'Painel executivo',
          icon: <AssignmentIcon />,
          path: '/executive',
          requiredRole: 'super_admin',
        },
        {
          text: 'Organizações',
          icon: <AssignmentIcon />,
          path: '/organizacoes',
          requiredRole: 'super_admin',
        },
        {
          text: 'Notificações',
          icon: <NotificationsIcon />,
          path: '/notificacoes',
          requiredRole: 'super_admin',
        },
      ],
    },
    {
      id: 'content',
      title: 'Conteúdo',
      collapsible: true,
      items: [
        {
          text: 'Habilidades BNCC',
          icon: <SchoolIcon />,
          path: '/habilidades',
          requiredPermission: null,
        },
        {
          text: 'Habilidades SAEB',
          icon: <SchoolIcon />,
          path: '/habilidades-saeb',
          requiredPermission: null,
        },
        {
          text: 'Habilidades ENEM',
          icon: <SchoolIcon />,
          path: '/habilidades-enem',
          requiredPermission: null,
        },
        {
          text: 'Áreas de conhecimento',
          icon: <CategoryIcon />,
          path: '/areas',
          requiredPermission: null,
        },
        {
          text: 'Prompts',
          icon: <SettingsIcon />,
          path: '/prompts',
          requiredPermission: 'gerenciarPrompts',
        },
        // 💰 ECONOMIA: "Base de conhecimento" (RAG/AWS Bedrock) só aparece se
        // REACT_APP_RAG_ENABLED === 'true'. Desligado para não expor a
        // funcionalidade enquanto o RAG está desativado no backend.
        ...(process.env.REACT_APP_RAG_ENABLED === 'true'
          ? [
              {
                text: 'Base de conhecimento',
                icon: <StorageIcon />,
                path: '/rag',
                requiredPermission: 'gerenciarRAG',
              },
            ]
          : []),
      ],
    },
    {
      id: 'questoes',
      title: 'Questões',
      collapsible: true,
      highlight: true,
      items: [
        {
          text: 'Questões INEP',
          icon: <QuizIcon />,
          path: '/questoes-inep',
          requiredPermission: 'gerarQuestoesInep',
        },
        {
          text: 'Questões Somos/Conecte',
          icon: <QuizIcon />,
          path: '/questoes-somos-conecte',
          requiredPermission: 'gerarQuestoesSomosConecte',
          organizationRestricted: 'Texto e Forma Conteúdo Educacional',
        },
        {
          text: 'Questões múltipla escolha 5 alternativas',
          icon: <QuizIcon />,
          path: '/questoes-multipla-escolha',
          requiredPermission: 'gerarQuestoesMultiplaEscolha',
        },
        {
          text: 'Questões abertas e formatos flexíveis',
          icon: <QuizIcon />,
          path: '/questoes-flexiveis',
          requiredPermission: 'gerarQuestoesFlexiveis',
        },
      ],
    },
    {
      id: 'roteiro',
      title: 'Roteiro',
      collapsible: true,
      highlight: true,
      items: [
        {
          text: 'Roteiros de vídeo',
          icon: <VideoIcon />,
          path: '/roteiros-video',
          requiredPermission: 'gerarRoteiroVideo',
        },
      ],
    },
    {
      id: 'texto',
      title: 'Texto',
      collapsible: true,
      highlight: true,
      items: [
        {
          text: 'Textos teóricos',
          icon: <BookIcon />,
          path: '/textos-teoricos',
          requiredPermission: 'gerarTextoTeorico',
        },
        {
          text: 'Orientação pedagógica',
          icon: <SchoolIcon />,
          path: '/orientacao-pedagogica',
          requiredPermission: 'gerarOrientacaoPedagogica',
        },
        {
          text: 'Texto alternativo',
          icon: <ImageSearchIcon />,
          path: '/texto-alternativo',
          requiredPermissions: ['gerarTextoAlternativo', 'gerarTextoTeorico'],
        },
      ],
    },
    {
      id: 'imagem',
      title: 'Imagem',
      collapsible: true,
      highlight: true,
      items: [
        {
          text: 'Geração de imagens',
          icon: <ImageIcon />,
          path: '/geracao-imagem',
        },
      ],
    },
    {
      id: 'plataforma_educativa',
      title: '1.14.003_PLATAFORMA_EDUCATIVA_ED',
      collapsible: true,
      highlight: true,
      organizationName: 'Texto e Forma Conteúdo Educacional',
      items: [
        {
          text: 'Diário de expedição',
          icon: <BookIcon />,
          path: '/diario-expedicao',
          requiredPermission: 'testarPrompts',
        },
        {
          text: 'Como aprendemos',
          icon: <SchoolIcon />,
          path: '/como-aprendemos',
          requiredPermission: 'testarPrompts',
        },
      ],
    },
    {
      id: 'outros',
      title: 'Outros',
      collapsible: true,
      highlight: true,
      items: [
        {
          text: 'Teste de prompts',
          icon: <ScienceIcon />,
          path: '/teste-prompts',
          requiredPermission: 'testarPrompts',
        },
        {
          text: 'Itens gerados',
          icon: <InventoryIcon />,
          path: '/itens',
          requiredPermission: null,
        },
      ],
    },
  ];

  // Filtrar seções baseado nas permissões
  const filteredSections = menuSections
    .map(section => {
      // Verificar se a seção é restrita a uma organização específica
      if (section.organizationId) {
        const isAllowed = 
          currentUser?.currentOrganization?._id === section.organizationId ||
          currentUser?.cargo === 'super_admin';
        
        if (!isAllowed) {
          return { ...section, items: [] };
        }
      }
      
      // Verificar por nome da organização
      if (section.organizationName) {
        const isAllowed = 
          currentUser?.currentOrganization?.name === section.organizationName ||
          currentUser?.cargo === 'super_admin';
        
        if (!isAllowed) {
          return { ...section, items: [] };
        }
        
        // Se a seção é permitida por organização, retornar todos os items sem filtro adicional
        return section;
      }
      
      return {
        ...section,
        items: section.items.filter(item => {
          // Verificar requiredRole primeiro
          if (item.requiredRole) {
            const hasRole = currentUser?.cargo === item.requiredRole || currentUser?.cargo === 'super_admin';
            if (!hasRole) return false;
          }
          
          if (item.requiredPermissions) {
            const hasAnyPermission = item.requiredPermissions.some(perm =>
              hasPermission(perm)
            );
            if (!hasAnyPermission) return false;
          } else if (item.requiredPermission) {
            if (!hasPermission(item.requiredPermission)) return false;
          }

          if (item.organizationRestricted) {
            return (
              currentUser?.currentOrganization?.name ===
                item.organizationRestricted ||
              currentUser?.cargo === 'super_admin'
            );
          }
          if (item.organizationModule) {
            return (
              currentUser?.currentOrganization?.modulosAtivos?.[item.organizationModule] ||
              currentUser?.cargo === 'super_admin'
            );
          }
          return (
            item.requiredPermission === null ||
            item.requiredPermissions ||
            hasPermission(item.requiredPermission)
          );
        }),
      };
    })
    .filter(section => section.items.length > 0);

  // Adicionar criação completa como seção separada se tiver permissão
  if (hasPermission('gerarCriacaoCompleta')) {
    const mainIndex = filteredSections.findIndex(s => s.id === 'main');
    if (mainIndex !== -1) {
      filteredSections.splice(mainIndex + 1, 0, {
        id: 'criacao-completa',
        items: [
          {
            text: 'Criação completa',
            icon: <CreateNewFolderIcon />,
            path: '/criacao-completa',
            requiredPermission: 'gerarCriacaoCompleta',
          },
        ],
      });
    }
  }

  const isActive = path =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  // Renderizar item do menu
  const renderMenuItem = item => {
    const active = isActive(item.path);
    const showText = !isMobile && desktopOpen;

    return (
      <Tooltip
        key={item.path}
        title={!showText ? item.text : ''}
        placement="right"
        arrow
      >
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={() => navigateTo(item.path)}
            selected={active}
            sx={{
              minHeight: { xs: 48, lg: 44 },
              justifyContent: showText ? 'initial' : 'center',
              px: { xs: 2.5, lg: 2 },
              mx: { xs: 1, lg: 0.5 },
              borderRadius: 2,
              border: 1,
              borderColor: 'transparent',
              transition: 'all 0.2s ease',
              color: 'text.primary',
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'white',
                borderColor: 'primary.main',
                '& .MuiListItemIcon-root': {
                  color: 'white',
                },
                '& .MuiListItemText-primary': {
                  color: 'white',
                },
                '& .MuiTypography-root': {
                  color: 'white',
                },
              },
              '&:hover': {
                backgroundColor: active
                  ? 'primary.dark'
                  : alpha(theme.palette.primary.main, 0.08),
                borderColor: alpha(theme.palette.primary.main, 0.3),
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: showText ? { xs: 3, lg: 2.5 } : 'auto',
                justifyContent: 'center',
                '& .MuiSvgIcon-root': {
                  fontSize: { xs: '1.25rem', lg: '1.1rem' },
                },
              }}
            >
              {item.icon}
            </ListItemIcon>

            {showText && (
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: active ? 600 : 500,
                        fontSize: { xs: '0.875rem', lg: '0.8rem' },
                      }}
                    >
                      {item.text}
                    </Typography>
                  </Box>
                }
                secondary={
                  item.description && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.75rem', lg: '0.7rem' } }} // Descrição menor em desktop
                    >
                      {item.description}
                    </Typography>
                  )
                }
              />
            )}
          </ListItemButton>
        </ListItem>
      </Tooltip>
    );
  };

  // Renderizar seção do menu
  const renderMenuSection = section => {
    const isExpanded = expandedSection === section.id;
    const showText = !isMobile && desktopOpen;
    const hasVisibleItems = section.items.length > 0;

    if (!hasVisibleItems) return null;

    return (
      <Box key={section.id} sx={{ mb: 1 }}>
        {section.collapsible && showText ? (
          <ListItemButton
            onClick={() => toggleSection(section.id)}
            sx={{
              minHeight: 36,
              px: 2.5,
              mx: 1,
              borderRadius: 1,
              backgroundColor: section.highlight
                ? alpha(theme.palette.primary.main, 0.04)
                : 'transparent',
            }}
          >
            <ListItemText
              primary={
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: 'text.secondary',
                  }}
                >
                  {section.title}
                </Typography>
              }
            />
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        ) : (
          showText && (
            <Box sx={{ px: 2.5, py: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: 'text.secondary',
                }}
              >
                {section.title}
              </Typography>
            </Box>
          )
        )}

        {section.collapsible ? (
          <Collapse in={!showText || isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {section.items.map(renderMenuItem)}
            </List>
          </Collapse>
        ) : (
          <List component="div" disablePadding>
            {section.items.map(renderMenuItem)}
          </List>
        )}
      </Box>
    );
  };

  // Conteúdo do drawer
  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header do drawer */}
      <Box
        sx={{
          p: { xs: 2, lg: 1.5 },
          minHeight: 57,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
        onClick={() => navigateTo('/dashboard')}
      >
        {(!isMobile && desktopOpen) || isMobile ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 32, height: 32 }}>
              <img
                src="/Logo_verso_46x46.svg"
                alt="Logo Verso"
                style={{ width: '100%', height: '100%' }}
              />
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 'bold',
                color: 'primary.main',
                fontSize: '32px',
                lineHeight: '32px',
              }}
            >
              Verso
            </Typography>
          </Box>
        ) : (
          <Box display="flex" justifyContent="center">
            <Box sx={{ width: { md: 36, lg: 40 }, height: { md: 36, lg: 40 } }}>
              <img
                src="/Logo_verso_46x46.svg"
                alt="Logo Verso"
                style={{ width: '100%', height: '100%' }}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Menu items */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', py: 1 }}>
        {filteredSections.map(renderMenuSection)}
      </Box>
    </Box>
  );

  return (
    <OrganizationChecker>
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <CssBaseline />

        {/* App Bar */}
        <AppBar
          position="fixed"
          elevation={1}
          sx={{
            width: { md: `calc(100% - ${drawerWidth}px)` },
            ml: { md: `${drawerWidth}px` },
            borderBottom: `1px solid ${theme.palette.divider}`,
            transition: theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}
        >
          <Toolbar>
            <IconButton
              sx={{ mr: 2 }}
              color="inherit"
              aria-label="toggle drawer"
              edge="start"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexGrow: 1,
              }}
            >
              <Box sx={{ width: 32, height: 32 }}>
                <img
                  src={
                    !isDark
                      ? '/Logo_verso_h_98x98_branca.svg'
                      : '/Logo_verso_46x46.svg'
                  }
                  alt="Logo Verso"
                  style={{ width: '100%', height: '100%' }}
                />
              </Box>
              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{
                  fontWeight: 600,
                  color: 'inherit',
                  fontSize: '1.5rem',
                }}
              >
                {isMobile || isTablet
                  ? 'Verso'
                  : 'Verso - Crie. Confie. Escale.'}
              </Typography>
            </Box>

            {/* Actions */}
            <Box display="flex" alignItems="center" gap={1}>
              {/* Botão para trocar organização */}
              {currentUser?.organizationIds?.length > 1 && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowOrgSelector(true)}
                  sx={{ mr: 1 }}
                >
                  {currentUser.currentOrganization?.name || 'Selecionar Org'}
                </Button>
              )}

              {/* Seletor de tema */}
              <ThemeSelector />

              {/* Notificações */}
              <NotificationBell />

              {/* Menu do usuário */}
              {currentUser && (
                <>
                  <IconButton
                    edge="end"
                    aria-label="conta do usuário"
                    onClick={handleProfileMenuOpen}
                    sx={{
                      ml: 1,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.grey[500], 0.1),
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: theme.palette.primary.main,
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      }}
                    >
                      {currentUser.nome.charAt(0)}
                    </Avatar>
                  </IconButton>

                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleProfileMenuClose}
                    TransitionComponent={Fade}
                    sx={{
                      '& .MuiPaper-root': {
                        borderRadius: 2,
                        minWidth: 200,
                        boxShadow:
                          '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body1" fontWeight={600}>
                        {currentUser.nome}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {currentUser.email}
                      </Typography>
                      <Chip
                        label={
                          currentUser.cargo.charAt(0).toUpperCase() +
                          currentUser.cargo.slice(1)
                        }
                        size="small"
                        variant="outlined"
                        sx={{ mt: 0.5, fontSize: '0.7rem' }}
                      />
                    </Box>

                    <MenuItem
                      onClick={() => {
                        handleProfileMenuClose();
                        navigate('/perfil');
                      }}
                    >
                      <ListItemIcon>
                        <AccountIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Perfil</ListItemText>
                    </MenuItem>

                    <MenuItem onClick={handleLogout}>
                      <ListItemIcon>
                        <LogoutIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Sair</ListItemText>
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>
          </Toolbar>
        </AppBar>

        {/* Drawer */}
        <Box
          component="nav"
          sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        >
          <Drawer
            variant={drawerVariant}
            open={drawerOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                transition: theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              },
            }}
          >
            {drawerContent}
          </Drawer>
        </Box>

        {/* Main content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: { md: `calc(100% - ${drawerWidth}px)` },
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            transition: theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}
        >
          <Toolbar /> {/* Spacer for fixed AppBar */}
          {/* Container com largura máxima controlada */}
          <Box
            sx={{
              flexGrow: 1,
              backgroundColor: theme.palette.background.default,
              display: 'flex',
              justifyContent: 'center',
              px: { xs: 2, sm: 3, lg: 4 },
              py: { xs: 2, sm: 3 },
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: {
                  xs: '100%', // Mobile: largura total
                  sm: '100%', // Tablet: largura total
                  md: '100%', // Desktop pequeno: largura total
                  lg: '1400px', // Desktop grande: máximo 1400px
                  xl: '1600px', // Desktop extra: máximo 1600px
                },
                mx: 'auto', // Centraliza o conteúdo
              }}
            >
              {children}
            </Box>
          </Box>
          {/* Footer */}
          <Box
            component="footer"
            sx={{
              py: { xs: 2, lg: 1.5 },
              px: { xs: 3, lg: 4 },
              textAlign: 'center',
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: { lg: '0.8rem' },
                fontWeight: 500,
              }}
            >
              © {new Date().getFullYear()} Verso - Todos os direitos reservados.
              v2.0
            </Typography>
          </Box>
        </Box>

        <OrganizationSelector
          open={showOrgSelector}
          onSelect={organizacao => {
            selectOrganization(organizacao);
            setShowOrgSelector(false);
          }}
          onClose={() => setShowOrgSelector(false)}
        />
      </Box>
    </OrganizationChecker>
  );
};

export default Layout;
